import {DialogsEnum} from "@/lib/enums/DialogsEnum";
import {DialogConfig} from "./types";

interface CooldownRecord {
    lastShownAt: number;
    lifetimeCount: number;
}

/** Pluggable persistence so the engine stays testable (inject a memory store). */
export interface CooldownStorage {
    getItem(key: string): string | null;
    setItem(key: string, value: string): void;
    removeItem(key: string): void;
}

const LS_KEY = "do.cooldowns.v1";
const SS_KEY = "do.session-counts.v1";

/**
 * Tracks frequency limits:
 *  - `cooldownMs`  → minimum gap between displays, persisted in localStorage
 *                    (survives reloads / new sessions, e.g. "PWA once / 24h").
 *  - `maxDisplays` → cap per `session` (sessionStorage) or `lifetime`
 *                    (localStorage), e.g. "marketing max 3 / session".
 *
 * All reads are defensive: storage can be unavailable (SSR, private mode), in
 * which case the manager degrades to in-memory only.
 */
export class CooldownManager {
    private readonly local: CooldownStorage;
    private readonly session: CooldownStorage;

    private lifetime: Record<string, CooldownRecord>;
    private sessionCounts: Record<string, number>;

    constructor(local?: CooldownStorage, session?: CooldownStorage) {
        this.local = local ?? safeStorage(() => window.localStorage);
        this.session = session ?? safeStorage(() => window.sessionStorage);
        this.lifetime = readJson(this.local, LS_KEY, {} as Record<string, CooldownRecord>);
        this.sessionCounts = readJson(this.session, SS_KEY, {} as Record<string, number>);
    }

    /** Whether the dialog is allowed to be shown right now w.r.t. throttling. */
    canShow(config: DialogConfig, now: number = Date.now()): boolean {
        const rec = this.lifetime[config.id];

        if (config.cooldownMs && rec && now - rec.lastShownAt < config.cooldownMs) {
            return false;
        }

        if (config.maxDisplays != null) {
            const scope = config.maxDisplaysScope ?? "session";
            const count = scope === "session"
                ? this.sessionCounts[config.id] ?? 0
                : rec?.lifetimeCount ?? 0;
            if (count >= config.maxDisplays) return false;
        }

        return true;
    }

    /** Record a successful display. Call exactly once, when the dialog opens. */
    registerDisplay(id: DialogsEnum, now: number = Date.now()): void {
        const rec = this.lifetime[id] ?? {lastShownAt: 0, lifetimeCount: 0};
        rec.lastShownAt = now;
        rec.lifetimeCount += 1;
        this.lifetime[id] = rec;
        this.sessionCounts[id] = (this.sessionCounts[id] ?? 0) + 1;

        writeJson(this.local, LS_KEY, this.lifetime);
        writeJson(this.session, SS_KEY, this.sessionCounts);
    }

    /** ms remaining before the dialog may show again (0 if available now). */
    remainingCooldown(config: DialogConfig, now: number = Date.now()): number {
        const rec = this.lifetime[config.id];
        if (!config.cooldownMs || !rec) return 0;
        return Math.max(0, config.cooldownMs - (now - rec.lastShownAt));
    }

    reset(id?: DialogsEnum): void {
        if (id) {
            delete this.lifetime[id];
            delete this.sessionCounts[id];
        } else {
            this.lifetime = {};
            this.sessionCounts = {};
        }
        writeJson(this.local, LS_KEY, this.lifetime);
        writeJson(this.session, SS_KEY, this.sessionCounts);
    }
}

function safeStorage(get: () => Storage): CooldownStorage {
    try {
        const s = get();
        // touch to trigger access errors early (private mode, SSR)
        const probe = "__do_probe__";
        s.setItem(probe, "1");
        s.removeItem(probe);
        return s;
    } catch {
        return memoryStorage();
    }
}

function memoryStorage(): CooldownStorage {
    const map = new Map<string, string>();
    return {
        getItem: (k) => map.get(k) ?? null,
        setItem: (k, v) => void map.set(k, v),
        removeItem: (k) => void map.delete(k),
    };
}

function readJson<T>(storage: CooldownStorage, key: string, fallback: T): T {
    try {
        const raw = storage.getItem(key);
        return raw ? (JSON.parse(raw) as T) : fallback;
    } catch {
        return fallback;
    }
}

function writeJson(storage: CooldownStorage, key: string, value: unknown): void {
    try {
        storage.setItem(key, JSON.stringify(value));
    } catch {
        /* quota / unavailable — ignore, throttling degrades to in-memory */
    }
}
